import React, { useState, useRef, useCallback, useEffect } from 'react';
import Stack from '@/components/Stack';
import Button from '@/components/Button';
import { Body, Small } from '@/components/Typography';

const PDFJS_CDN = 'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.min.mjs';
const PDFJS_WORKER_CDN =
  'https://unpkg.com/pdfjs-dist@4.4.168/build/pdf.worker.min.mjs';

const SIGNATURE_BOX_WIDTH = 150;
const SIGNATURE_BOX_HEIGHT = 40;

// Lazy-loaded pdfjs library
let pdfjsLib: any = null;

async function loadPdfJs(): Promise<any> {
  if (pdfjsLib) return pdfjsLib;
  pdfjsLib = await import(/* webpackIgnore: true */ PDFJS_CDN);
  pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
  return pdfjsLib;
}

interface PdfSignaturePlacerProps {
  pdfUrl: string;
  initialPage?: number;
  initialX?: number | null;
  initialY?: number | null;
  onPositionChange: (page: number, x: number, y: number) => void;
}

const PdfSignaturePlacer: React.FC<PdfSignaturePlacerProps> = ({
  pdfUrl,
  initialPage = 1,
  initialX,
  initialY,
  onPositionChange,
}) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(initialPage);
  const [pageWidth, setPageWidth] = useState<number>(612);
  const [pageHeight, setPageHeight] = useState<number>(792);
  const [scale, setScale] = useState<number>(1);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(true);

  // Drag state — position is in PDF points (origin bottom-left)
  const [pdfX, setPdfX] = useState<number>(initialX ?? 200);
  const [pdfY, setPdfY] = useState<number>(initialY ?? 100);
  const [dragging, setDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const pdfDocRef = useRef<any>(null);

  // Convert PDF points (bottom-left origin) to screen pixels (top-left origin)
  const pdfToScreen = useCallback(
    (px: number, py: number) => ({
      screenX: px * scale,
      screenY: (pageHeight - py) * scale - SIGNATURE_BOX_HEIGHT,
    }),
    [scale, pageHeight],
  );

  // Convert screen pixels (top-left origin) to PDF points (bottom-left origin)
  const screenToPdf = useCallback(
    (sx: number, sy: number) => ({
      pdfX: sx / scale,
      pdfY: pageHeight - (sy + SIGNATURE_BOX_HEIGHT) / scale,
    }),
    [scale, pageHeight],
  );

  // Load PDF document
  useEffect(() => {
    let cancelled = false;

    async function loadPdf() {
      setPdfLoading(true);
      setPdfError(null);
      try {
        const lib = await loadPdfJs();
        const doc = await lib.getDocument(pdfUrl).promise;
        if (cancelled) return;
        pdfDocRef.current = doc;
        setNumPages(doc.numPages);
        if (initialPage > doc.numPages) {
          setCurrentPage(1);
        }
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to load PDF: ${err.message || err}`);
        }
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    }

    loadPdf();
    return () => {
      cancelled = true;
    };
  }, [pdfUrl]);

  // Render current page to canvas
  useEffect(() => {
    const doc = pdfDocRef.current;
    if (!doc || currentPage < 1 || currentPage > numPages) return;

    let cancelled = false;

    async function renderPage() {
      try {
        const page = await doc.getPage(currentPage);
        if (cancelled) return;

        const viewport = page.getViewport({ scale: 1 });
        setPageWidth(viewport.width);
        setPageHeight(viewport.height);

        // Fit to container width
        let newScale = 1;
        if (containerRef.current) {
          const containerWidth = containerRef.current.clientWidth - 32;
          newScale = Math.min(containerWidth / viewport.width, 1.5);
        }
        setScale(newScale);

        const scaledViewport = page.getViewport({ scale: newScale });
        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        canvas.width = scaledViewport.width;
        canvas.height = scaledViewport.height;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        await page.render({ canvasContext: ctx, viewport: scaledViewport })
          .promise;
      } catch (err: any) {
        if (!cancelled) {
          setPdfError(`Failed to render page: ${err.message || err}`);
        }
      }
    }

    renderPage();
    return () => {
      cancelled = true;
    };
  }, [numPages, currentPage]);

  // Fire position change whenever coords or page changes
  useEffect(() => {
    onPositionChange(
      currentPage,
      Math.round(pdfX * 10) / 10,
      Math.round(pdfY * 10) / 10,
    );
  }, [currentPage, pdfX, pdfY]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const { screenX, screenY } = pdfToScreen(pdfX, pdfY);
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Check if click is inside the signature box
      if (
        mouseX >= screenX &&
        mouseX <= screenX + SIGNATURE_BOX_WIDTH &&
        mouseY >= screenY &&
        mouseY <= screenY + SIGNATURE_BOX_HEIGHT
      ) {
        setDragging(true);
        setDragOffset({ x: mouseX - screenX, y: mouseY - screenY });
      }
    },
    [pdfX, pdfY, pdfToScreen],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!dragging) return;
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const newScreenX = Math.max(
        0,
        Math.min(
          mouseX - dragOffset.x,
          pageWidth * scale - SIGNATURE_BOX_WIDTH,
        ),
      );
      const newScreenY = Math.max(
        0,
        Math.min(
          mouseY - dragOffset.y,
          pageHeight * scale - SIGNATURE_BOX_HEIGHT,
        ),
      );

      const converted = screenToPdf(newScreenX, newScreenY);
      setPdfX(converted.pdfX);
      setPdfY(converted.pdfY);
    },
    [dragging, dragOffset, pageWidth, pageHeight, scale, screenToPdf],
  );

  const handleMouseUp = useCallback(() => {
    setDragging(false);
  }, []);

  // Click-to-place: clicking on the PDF (not the box) moves the box there
  const handlePageClick = useCallback(
    (e: React.MouseEvent) => {
      if (dragging) return;
      const rect = pageContainerRef.current?.getBoundingClientRect();
      if (!rect) return;

      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      const newScreenX = Math.max(
        0,
        Math.min(
          mouseX - SIGNATURE_BOX_WIDTH / 2,
          pageWidth * scale - SIGNATURE_BOX_WIDTH,
        ),
      );
      const newScreenY = Math.max(
        0,
        Math.min(
          mouseY - SIGNATURE_BOX_HEIGHT / 2,
          pageHeight * scale - SIGNATURE_BOX_HEIGHT,
        ),
      );

      const converted = screenToPdf(newScreenX, newScreenY);
      setPdfX(converted.pdfX);
      setPdfY(converted.pdfY);
    },
    [dragging, pageWidth, pageHeight, scale, screenToPdf],
  );

  const { screenX, screenY } = pdfToScreen(pdfX, pdfY);

  return (
    <div ref={containerRef}>
      <Stack gap={8} style={{ marginBottom: '12px' }}>
        <Button
          a11yLabel="Previous page"
          appearance="outline"
          label="Prev"
          disabled={currentPage <= 1}
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
        />
        <Body>
          Page {currentPage} of {numPages || '...'}
        </Body>
        <Button
          a11yLabel="Next page"
          appearance="outline"
          label="Next"
          disabled={currentPage >= numPages}
          onClick={() => setCurrentPage((p) => Math.min(numPages, p + 1))}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Small>
            Position: Page {currentPage}, X: {pdfX.toFixed(1)}, Y:{' '}
            {pdfY.toFixed(1)}
          </Small>
        </div>
      </Stack>

      <div
        style={{
          border: '1px solid #ddd',
          borderRadius: '4px',
          overflow: 'auto',
          maxHeight: '600px',
          background: '#f5f5f5',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        {pdfLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <Body>Loading PDF...</Body>
          </div>
        ) : pdfError ? (
          <div style={{ padding: '20px', color: 'red' }}>
            <Body>{pdfError}</Body>
          </div>
        ) : (
          <div
            ref={pageContainerRef}
            style={{
              position: 'relative',
              cursor: dragging ? 'grabbing' : 'crosshair',
              userSelect: 'none',
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onClick={handlePageClick}
          >
            <canvas ref={canvasRef} style={{ display: 'block' }} />
            {/* Signature placement box */}
            <div
              style={{
                position: 'absolute',
                left: `${screenX}px`,
                top: `${screenY}px`,
                width: `${SIGNATURE_BOX_WIDTH}px`,
                height: `${SIGNATURE_BOX_HEIGHT}px`,
                border: '2px dashed #e53e3e',
                borderRadius: '4px',
                backgroundColor: 'rgba(229, 62, 62, 0.1)',
                cursor: dragging ? 'grabbing' : 'grab',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'auto',
                zIndex: 10,
              }}
            >
              <span
                style={{
                  fontSize: '11px',
                  color: '#e53e3e',
                  fontWeight: 600,
                  userSelect: 'none',
                }}
              >
                Signature Here
              </span>
            </div>
          </div>
        )}
      </div>

      <div style={{ marginTop: '8px' }}>
        <Small>
          Click anywhere on the PDF to place the signature, or drag the box to
          reposition it.
        </Small>
      </div>
    </div>
  );
};

export default PdfSignaturePlacer;
